import { Component, ElementRef, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CourseService } from 'src/app/services/course.service';
import { NotificationService } from 'src/app/services/notification.service';

@Component({
  selector: 'app-course-work-rerun-autograde',
  templateUrl: './course-work-rerun-autograde.component.html',
  styleUrls: ['./course-work-rerun-autograde.component.scss']
})
export class CourseWorkRerunAutogradeComponent implements OnInit {

  @Input() loader: boolean;
  @Input() courseworks: any;
  @Input() courseId: string = '';
  @Input() courseworksSubmission: any;
  @Input() submissionsData: any;
  @Input() isSubmissionPublish: any;
  @Output() onRerunAutogradeComplete: EventEmitter<any> = new EventEmitter<any>();
  courseWorkId: string = '';
  scheduleId: string = '';
  courseworkTeaminfo: any = [];
  markingTeamInfo: any[] = [];
  markingUserInfo: any[] = [];
  teamName: string = '';
  userId: string = '';
  allSubmissions: any = {};
  role: string = '';

  isRerunAutogradingAllowed: boolean = false;
  rerunAutogradeStatus: number = 0;
  rerunAutogradeStatusMsg: string = '';

  isLoading: boolean = false;
  rerunAutogradingModalRef: any = null;
  @ViewChild('rerunAutogradingModal', { static: false }) rerunAutogradingModal!: ElementRef;
  rerunAutogradingForm!: FormGroup;
  submitRerunAutogradingForm: boolean = false;

  constructor(private modalService: NgbModal, private fb: FormBuilder, private ns: NotificationService,
    private courseService: CourseService, private route: ActivatedRoute) { }

  /**
   * To initialize component data
   */
  ngOnInit() {
    this.role = localStorage.getItem('role');
    this.route.queryParams.subscribe(params => {
      this.teamName = params.teamName;
      this.userId = params.userId;
    });
  }

  /**
   * To detect input data changes.
   * @param {SimpleChanges} changes - To get input data
   */
  ngOnChanges(changes: SimpleChanges) {
    if ('courseworksSubmission' in changes) {
      this.initRerunAutogradingForm();
      this.courseWorkId = this.courseworks._id;
      this.scheduleId = this.courseworks.schedule_id;
      this.rerunAutogradingForm.get('course_id').setValue(this.courseId);
      this.rerunAutogradingForm.get('coursework_id').setValue(this.courseWorkId);
      this.rerunAutogradingForm.get('coursework_instance_id').setValue(this.scheduleId);
    }
    if ('submissionsData' in changes) {
      if (!this.isSubmissionPublish && this.courseworks.is_submission_question && this.courseworks.autograde_pipeline_status == 2 && ["teacher", "super_admin"].includes(this.role)) {
        const markingInfo = this.getMarkingData();
        if (Object.keys(markingInfo).length > 0) {
          if ([0, 2].includes(markingInfo['rerun_autograde_status']) && markingInfo['marked_status'] == true) {
            this.allSubmissions = markingInfo['submissions'];
            this.isRerunAutogradingAllowed = true;
          }
          this.rerunAutogradeStatus = markingInfo['rerun_autograde_status'];
          if (this.rerunAutogradeStatus === 1) {
            this.rerunAutogradeStatusMsg = 'Autograding in progress.';
          }
          else if (this.rerunAutogradeStatus === -1) {
            this.rerunAutogradeStatusMsg = 'The rerurn autograding process encountered an error. Please reach out to your administrator for help.';
          }
          else if (this.rerunAutogradeStatus === 2) {
            this.rerunAutogradeStatusMsg = 'Autograding completed.';
          }
        }
      }
    }
    this.rerunAutogradingForm.get('user_id').setValue(this.userId || '');
    this.rerunAutogradingForm.get('team_name').setValue(this.teamName || '');
  }

  /**
   * To get selected users marking data
   */
  getMarkingData() {
    let markingData = {};
    let findedData: any = {};
    if (this.courseworks.is_group == true) {
      findedData = this.submissionsData.find(item => {
        return item.name == this.teamName;
      });
    }
    else {
      findedData = this.submissionsData.find(item => {
        return item.user_id == this.userId;
      });
    }

    if (findedData) {
      markingData = findedData;
    }

    return markingData;
  }

  /**
   * To initialize rerun autograding form
   */
  initRerunAutogradingForm() {
    this.rerunAutogradingForm = this.fb.group({
      course_id: ['', Validators.required],
      coursework_id: ['', Validators.required],
      coursework_instance_id: ['', Validators.required],
      user_id: [''],
      team_name: [''],
      selected_submissions: this.fb.array([], this.minSelectedSubmissions(1))
    });
  }

  /**
   * To add custom validation on select submissions field
    * @param {number} min - To get min validation value
   */
  minSelectedSubmissions(min: number = 1) {
    return function (formArray: AbstractControl): ValidationErrors | null {
      const selected = (formArray.value || []).filter((v: any) => v !== false);
      return selected.length >= min ? null : { required: true };
    };
  }

  /**
   * To open rerun autograding modal
   */
  openRerunAutogradingModal() {
    this.rerunAutogradingModalRef = this.modalService.open(this.rerunAutogradingModal, { size: 'md', backdrop: 'static' });
  }

  /**
   * To close rerun autograding modal
   */
  closeRerunAutogradingModal() {
    this.submitRerunAutogradingForm = false;
    this.rerunAutogradingModalRef.close();
  }

  /**
   * To get selected submissions for rerun autograding
   */
  onCheckboxChange(event: any, submission: any) {
    const checkbox = event.target as HTMLInputElement;
    const selectedSubmissions = this.rerunAutogradingForm.get('selected_submissions') as FormArray;

    if (checkbox.checked) {
      selectedSubmissions.push(new FormControl(submission.submission_report));
    } else {
      const index = selectedSubmissions.controls.findIndex(x => x.value === submission.submission_report);
      if (index !== -1) {
        selectedSubmissions.removeAt(index);
      }
    }
  }

  /**
   * To start rerun autograding for selected submissions
   */
  onSaveRerunAutograding() {
    this.submitRerunAutogradingForm = true;
    if (this.rerunAutogradingForm.invalid) {
      this.ns.showNotification('error', 'The form is not valid. Please provide all the required information.');
      return false;
    }
    const postData = { ...this.rerunAutogradingForm.value };
    this.isLoading = true;
    this.courseService.rerunAutograding(postData).subscribe((response: any) => {
      this.isLoading = false;
      this.submitRerunAutogradingForm = false;
      if (Object.keys(response.data).length > 0) {
        if (response.data.rerun_autograde_status == 1) {
          this.rerunAutogradeStatus = response.data.rerun_autograde_status;
          this.rerunAutogradeStatusMsg = response.data.rerun_autograde_status_msg;
          this.isRerunAutogradingAllowed = false;
          this.closeRerunAutogradingModal();
          this.ns.showNotification('success', 'Autograding job has started.');
          this.onRerunAutogradeComplete.emit({ status: true });
        }
        else {
          this.ns.showNotification('error', 'Autograding process failed.');
        }
      }
      else {
        this.ns.showNotification('error', 'Something went wrong. Please try again later.');
      }
    }, (error: any) => {
      this.isLoading = false;
      this.submitRerunAutogradingForm = false;
      this.ns.showNotification('error', error.message);
    });
  }

}
